import { useState } from "react";
import { LOGO_PATH } from "../../Components/Brand/LogoMark";
import { TECH } from "../../content/tech";

/* Geometry (SVG user units) */
const VB_W = 1200;
const VB_H = 700;
const CX = 600;
const CY = 350;
/* The logo path sits on a 50×40 grid (x 6–46, y 6–37); scale it into the middle. */
const SCALE = 13;
const TX = CX - 26 * SCALE;
const TY = CY - 21.5 * SCALE;
/* Tools sit in two curved columns that hug the loop: x bows out at mid-height. */
const ROW = 29;
const BOW_MIN = 200;
const BOW = 200;
const BOW_RY = 330;

type Row =
  | { kind: "head"; cat: string; x: number; y: number; side: 1 | -1 }
  | { kind: "tool"; cat: string; name: string; x: number; y: number; side: 1 | -1 };

/** Right column: first half of the categories; left column: the rest. Each starts with its category name. */
const placeRows = (): Row[] => {
  const half = Math.ceil(TECH.length / 2);
  const sides: [typeof TECH, 1 | -1][] = [
    [TECH.slice(0, half), 1],
    [TECH.slice(half), -1],
  ];
  const out: Row[] = [];
  sides.forEach(([cats, side]) => {
    const n = cats.reduce((sum, c) => sum + c.items.length + 1, 0);
    let k = 0;
    const at = () => {
      const y = CY + (k - (n - 1) / 2) * ROW;
      const t = (y - CY) / BOW_RY;
      k += 1;
      return { x: CX + side * (BOW_MIN + BOW * Math.sqrt(Math.max(0, 1 - t * t))), y };
    };
    cats.forEach((c) => {
      out.push({ kind: "head", cat: c.name, side, ...at() });
      c.items.forEach((name) => out.push({ kind: "tool", cat: c.name, name, side, ...at() }));
    });
  });
  return out;
};

const ROWS = placeRows();

/**
 * Technologies page centrepiece: the i∞ loop drawn once, every tool placed
 * around it, and a category picker (text links on a dotted line, like the
 * header nav). Picking a category lights its tools in coral and dims the
 * rest. Phones: the loop plus a plain list of the selected tools.
 */
const TechLoop = () => {
  const [active, setActive] = useState<string | null>(null);
  const shown = active ? TECH.filter((c) => c.name === active) : TECH;

  return (
    <section className="is-container is-techloop" aria-labelledby="techloop-title">
      <h2 id="techloop-title" className="is-techloop__sr">
        Technologies by category
      </h2>

      <div className="is-picker" role="group" aria-label="Filter by category">
        <button
          type="button"
          className={"is-picker__item" + (active === null ? " is-active" : "")}
          aria-pressed={active === null}
          onClick={() => setActive(null)}
        >
          All
        </button>
        {TECH.map((c) => (
          <button
            key={c.name}
            type="button"
            className={"is-picker__item" + (active === c.name ? " is-active" : "")}
            aria-pressed={active === c.name}
            onClick={() => setActive(active === c.name ? null : c.name)}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className={"is-techloop__stage" + (active ? " has-active" : "")} aria-hidden="true">
        <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%">
          {/* the loop, drawn once */}
          <g transform={`translate(${TX} ${TY}) scale(${SCALE})`}>
            <path d={LOGO_PATH} className="is-techloop__track" />
            <path d={LOGO_PATH} className="is-techloop__centre" />
            <circle cx="6" cy="7.5" r="1.6" className="is-techloop__i-dot" />
            <circle r="0.9" className="is-techloop__runner">
              <animateMotion dur="7s" repeatCount="indefinite" path={LOGO_PATH} />
            </circle>
          </g>
          {/* every tool around it, in two curved columns */}
          <g className="is-techloop__ring">
            {ROWS.map((r) =>
              r.kind === "head" ? (
                <text
                  key={"h-" + r.cat}
                  x={r.x + r.side * 2}
                  y={r.y + 4}
                  textAnchor={r.side === 1 ? "start" : "end"}
                  className={"is-techloop__head" + (active === r.cat ? " is-lit" : "")}
                >
                  {r.cat}
                </text>
              ) : (
                <g
                  key={r.name}
                  className={"is-techloop__tool" + (active === r.cat ? " is-lit" : "")}
                >
                  <circle cx={r.x + r.side * 8} cy={r.y} r="5" />
                  <text x={r.x + r.side * 22} y={r.y + 6} textAnchor={r.side === 1 ? "start" : "end"}>
                    {r.name}
                  </text>
                </g>
              )
            )}
          </g>
        </svg>
      </div>

      {/* Phones (and screen readers): the same tools as text */}
      <dl className="is-techloop__list">
        {shown.map((c) => (
          <div key={c.name} className="is-techloop__list-row">
            <dt>{c.name}</dt>
            <dd>{c.items.join(" · ")}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default TechLoop;
